import {
  ReactElement,
  SyntheticEvent,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { Stack } from '@mui/material';
import { TabContext, TabList } from '@mui/lab';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useFormik, FormikProvider, FormikHelpers } from 'formik';
import moment from 'moment';
import { omit } from 'lodash';

import { Nullable } from '~globals/types';
import { RouteItemStateType } from '~globals/types/enums';
import { getItemValue } from '~utils/helpers';
import { formatterDate } from '~utils/formatter';
import { hasError, hasErrorOnFieldArray } from '~utils/formHelpers';
import { RouteItemFeedbackSetItemTasks } from '~services/route/types';
import { ItemCollect } from '~services/item/types';

import { useDialogFeedbackStopContext } from '../../DialogSetFeedbackStopContext';
import DialogSetFeedbackStopContent from '../../DialogSetFeedbackStopContent';
import DialogSetFeebackStopResetStep from '../../DialogSetFeebackStopResetStep';

import {
  FIELDS_NAME,
  ITEM_TASKS_FIELDS_NAME,
  ITEM_COLLECTS_FIELDS_NAME,
  getInitialValues,
  getValidationSchema,
} from './utils';
import {
  DialogSetFeedbackStopCompletedEvidence,
  DialogSetFeedbackStopCompletedItemTasks,
  DialogSetFeedbackStopCompletedItemCollects,
} from './Tabs';
import {
  DialogSetFeedbackStopCompletedAppBar,
  DialogSetFeedbackStopCompletedTab,
  DialogSetFeedbackStopCompletedTabPanel,
} from './styles';

const DialogSetFeedbackStopCompleted = (): ReactElement => {
  const {
    routeItemId,
    data,
    isFeedbackRequestAudit,
    isEnabledAudit,
    isModify,
    setItemFeedbackRequest: { loading: loadingSetFeedback, handleSetFeedback },
  } = useDialogFeedbackStopContext();

  const isCollectItem = useMemo(() => data?.isCollectItem ?? false, [data]);

  const isWarehouse = useMemo(() => data?.isWarehouse ?? false, [data]);

  const isRequiredAudit = useMemo(
    () => Boolean(isEnabledAudit) && isFeedbackRequestAudit,
    [isEnabledAudit, isFeedbackRequestAudit],
  );

  const onSubmit = useCallback(
    async (
      values: ReturnType<typeof getInitialValues>,
      { setSubmitting }: FormikHelpers<ReturnType<typeof getInitialValues>>,
    ) => {
      const routeItemStateTypeId = RouteItemStateType.FinalizedSuccess;

      const arrivalDate = formatterDate(values[FIELDS_NAME.ARRIVAL_DATE], {
        format: moment.HTML5_FMT.DATETIME_LOCAL_MS,
      });

      const deliveredDate = data?.deliveredDate
        ? formatterDate(data.deliveredDate, {
            format: moment.HTML5_FMT.DATETIME_LOCAL_MS,
          })
        : arrivalDate;

      const amountPaid = getItemValue(
        values,
        FIELDS_NAME.AMOUNT_PAID,
        (value) => Number(value),
      ) as Nullable<number>;

      const itemTasks = getItemValue(
        values,
        FIELDS_NAME.ITEM_TASKS,
        (tasks): RouteItemFeedbackSetItemTasks[] =>
          tasks.map((task) => omit(task, [ITEM_TASKS_FIELDS_NAME.IS_OWNER])),
        [] as RouteItemFeedbackSetItemTasks[],
      );

      const itemCollects = getItemValue(
        values,
        FIELDS_NAME.ITEM_COLLECTS,
        (collects): ItemCollect[] =>
          collects.map((collect) =>
            omit(collect, [ITEM_COLLECTS_FIELDS_NAME.IS_OWNER]),
          ),
        [] as ItemCollect[],
      );

      let auditUnit: Nullable<number> = null;

      if (isRequiredAudit) {
        auditUnit = Number(values[FIELDS_NAME.AUDIT_UNIT]);
      }

      await handleSetFeedback({
        routeItemId,
        routeItemStateTypeId,
        arrivalDate,
        deliveredDate,
        amountPaid,
        docsFiles: values[FIELDS_NAME.DOCS_FILES],
        evidencesFiles: values[FIELDS_NAME.EVIDENCE_FILES],
        receptorName: getItemValue(values, FIELDS_NAME.RECEPTOR_NAME),
        receptorDocument: getItemValue(values, FIELDS_NAME.RECEPTOR_DOC),
        notes: getItemValue(values, FIELDS_NAME.NOTES),
        rejectedReason: null,
        itemTasks,
        itemCollects,
        isCollectItem,
        isModify,
        auditUnit,
      });

      setSubmitting(false);
    },
    [
      data,
      handleSetFeedback,
      routeItemId,
      isCollectItem,
      isModify,
      isRequiredAudit,
    ],
  );

  const formikBag = useFormik({
    initialValues: getInitialValues(data),
    validationSchema: getValidationSchema(isRequiredAudit),
    onSubmit,
    validateOnMount: true,
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { submitForm, isSubmitting, dirty, errors, touched } = formikBag;

  const hasErrorOnEvidence = useMemo(() => {
    const baseValidation = hasError(touched, errors, FIELDS_NAME.ARRIVAL_DATE);

    if (isRequiredAudit) {
      return (
        baseValidation || hasError(touched, errors, FIELDS_NAME.AUDIT_UNIT)
      );
    }

    return baseValidation;
  }, [errors, isRequiredAudit, touched]);

  const hasErrorOnItemTasks = useMemo(() => {
    if (isCollectItem) return false;

    return hasErrorOnFieldArray(touched, errors, FIELDS_NAME.ITEM_TASKS);
  }, [errors, isCollectItem, touched]);

  const hasErrorOnItemCollects = useMemo(() => {
    if (!isCollectItem) return false;

    return hasErrorOnFieldArray(touched, errors, FIELDS_NAME.ITEM_COLLECTS);
  }, [errors, isCollectItem, touched]);

  const tabsList = useMemo(
    () => [
      {
        id: 'evidence',
        label: 'Evidencia',
        enabled: true,
        error: hasErrorOnEvidence,
      },
      {
        id: 'itemTasks',
        label: 'Articulo / Tareas',
        enabled: !isCollectItem,
        error: hasErrorOnItemTasks,
      },
      {
        id: 'itemCollects',
        label: 'Pedidos',
        enabled: isCollectItem,
        error: hasErrorOnItemCollects,
      },
    ],
    [
      hasErrorOnEvidence,
      hasErrorOnItemCollects,
      hasErrorOnItemTasks,
      isCollectItem,
    ],
  );

  const tabsListPanels = useMemo(
    () => [
      {
        id: tabsList[0].id,
        panel: DialogSetFeedbackStopCompletedEvidence,
        enabled: tabsList[0].enabled,
      },
      {
        id: tabsList[1].id,
        panel: DialogSetFeedbackStopCompletedItemTasks,
        enabled: tabsList[1].enabled,
      },
      {
        id: tabsList[2].id,
        panel: DialogSetFeedbackStopCompletedItemCollects,
        enabled: tabsList[2].enabled,
      },
    ],
    [tabsList],
  );

  const [activeTab, setActiveTab] = useState(tabsList[0].id);

  const handleChangeActiveTab = useCallback(
    (_e: SyntheticEvent, newValue: string) => {
      setActiveTab(newValue);
    },
    [],
  );

  return (
    <DialogSetFeedbackStopContent
      onSubmit={submitForm}
      loadingSubmit={loadingSetFeedback || isSubmitting}
      // disabledSubmit={isModify && !dirty}
    >
      <FormikProvider value={formikBag}>
        <Stack spacing={2} flex={1} height="100%">
          <DialogSetFeebackStopResetStep />

          <TabContext value={activeTab}>
            {!isWarehouse && (
              <DialogSetFeedbackStopCompletedAppBar
                position="static"
                color="tertiary"
                elevation={0}
              >
                <TabList
                  onChange={handleChangeActiveTab}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  {tabsList.map(
                    ({ label, id, enabled, error }) =>
                      enabled && (
                        <DialogSetFeedbackStopCompletedTab
                          key={`dialog-set-feedback-stop-completed-tab-${id}`}
                          label={label}
                          value={id}
                          icon={
                            error ? (
                              <ErrorOutlineIcon color="error" />
                            ) : undefined
                          }
                          iconPosition="end"
                        />
                      ),
                  )}
                </TabList>
              </DialogSetFeedbackStopCompletedAppBar>
            )}

            {tabsListPanels.map(
              ({ id, panel: Panel, enabled }) =>
                enabled && (
                  <DialogSetFeedbackStopCompletedTabPanel
                    key={`dialog-set-feedback-stop-completed-tab-panel-${id}`}
                    value={id}
                  >
                    <Panel />
                  </DialogSetFeedbackStopCompletedTabPanel>
                ),
            )}
          </TabContext>
        </Stack>
      </FormikProvider>
    </DialogSetFeedbackStopContent>
  );
};

export default DialogSetFeedbackStopCompleted;
